/**
 * Iter-235e — Agency Subscription success / fallback activation page.
 *
 * After Stripe Checkout, the agency lands here with `?session_id=...`.
 * We immediately call `POST /api/agency/subscriptions/confirm` so the
 * subscription activates even if the webhook lags or never lands.
 *
 * Why this exists:
 *   Before this page, the `success_url` from `/api/agency/subscribe/{plan_id}`
 *   pointed at a route that didn't exist — every agency checkout
 *   landed on a 404 and the subscription stayed `pending` in
 *   `subscription_transactions` indefinitely.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, AlertCircle, RefreshCcw } from 'lucide-react';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

export default function AgencySubscriptionSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get('session_id');

  const [state, setState] = useState({ phase: 'confirming', planId: null, error: null });

  const confirm = async () => {
    if (!sessionId) {
      setState({ phase: 'error', error: 'Missing session id in URL.' });
      return;
    }
    setState((s) => ({ ...s, phase: 'confirming', error: null }));
    try {
      const r = await api.post(`/agency/subscriptions/confirm?session_id=${encodeURIComponent(sessionId)}`);
      if (r.data?.activated) {
        setState({ phase: 'success', planId: r.data.plan_id, error: null });
      } else {
        setState({ phase: 'pending', planId: r.data?.plan_id, error: r.data?.message || 'Payment still processing.' });
      }
    } catch (e) {
      setState({ phase: 'error', error: e.response?.data?.detail || e.message || 'Activation failed.' });
    }
  };

  useEffect(() => { confirm(); /* eslint-disable-next-line */ }, [sessionId]);

  return (
    <div className="max-w-md mx-auto p-6" data-testid="agency-subscription-success">
      <Card>
        <CardContent className="p-6 text-center space-y-4">
          {state.phase === 'confirming' && (
            <>
              <Loader2 className="w-10 h-10 text-cyan-600 animate-spin mx-auto" />
              <h1 className="text-lg font-semibold text-slate-900">Activating your subscription…</h1>
              <p className="text-sm text-slate-600">Hang tight — we&apos;re confirming the payment with Stripe.</p>
            </>
          )}

          {state.phase === 'success' && (
            <>
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
              <h1 className="text-lg font-bold text-slate-900">You&apos;re all set!</h1>
              <p className="text-sm text-slate-600">
                {state.planId
                  ? <>Your <span className="font-semibold capitalize">{state.planId}</span> plan is active.</>
                  : 'Your agency subscription is active.'}
              </p>
              <Button onClick={() => navigate('/agency/dashboard')} className="bg-cyan-600 hover:bg-cyan-700" data-testid="success-continue">
                Continue to dashboard
              </Button>
            </>
          )}

          {state.phase === 'pending' && (
            <>
              <Loader2 className="w-10 h-10 text-amber-500 animate-spin mx-auto" />
              <h1 className="text-lg font-semibold text-slate-900">Payment still processing</h1>
              <p className="text-sm text-slate-600">
                Stripe hasn&apos;t finished confirming the payment yet. We&apos;ll keep retrying.
              </p>
              <div className="flex justify-center gap-2">
                <Button variant="outline" onClick={confirm} className="gap-1.5" data-testid="success-retry">
                  <RefreshCcw className="w-3 h-3" /> Retry now
                </Button>
                <Button variant="ghost" onClick={() => navigate('/agency/dashboard')}>Go to dashboard</Button>
              </div>
            </>
          )}

          {state.phase === 'error' && (
            <>
              <AlertCircle className="w-10 h-10 text-rose-500 mx-auto" />
              <h1 className="text-lg font-semibold text-slate-900">We couldn&apos;t activate your plan</h1>
              <p className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2">
                {state.error}
              </p>
              <p className="text-xs text-slate-500">
                If you were charged, your subscription will still be applied automatically when Stripe confirms.
              </p>
              <div className="flex justify-center gap-2">
                <Button variant="outline" onClick={confirm} className="gap-1.5" data-testid="success-retry">
                  <RefreshCcw className="w-3 h-3" /> Retry
                </Button>
                <Button variant="ghost" onClick={() => navigate('/agency/dashboard')}>Go to dashboard</Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
